/**
 * Stone Investment Pagination
 *
 * Stone-themed wrapper for the core Pagination component.
 * Applies greyscale palette, square corners, editorial typography,
 * and Stone's luxury aesthetic via CSS overrides.
 */

'use client';

import { Pagination, type PaginationProps } from '@/components/ui/Pagination';

export type StonePaginationProps = PaginationProps & {
  messages?: Record<string, unknown>;
  getPageHref?: (page: number) => string;
};

export function StonePagination({
  messages,
  labels,
  getPageHref,
  ...props
}: StonePaginationProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const paginationMessages = (stoneMessages.pagination || {}) as Record<string, string>;

  const resolvedLabels = {
    previous: labels?.previous || paginationMessages.previous || 'Previous',
    next: labels?.next || paginationMessages.next || 'Next',
    first: labels?.first || paginationMessages.first || 'First',
    last: labels?.last || paginationMessages.last || 'Last',
  };

  // Real links work without hydration and expose later inventory to crawlers.
  if (getPageHref) {
    if (props.totalPages <= 1) return null;
    const pages = Array.from(new Set([
      1, props.totalPages,
      ...Array.from({ length: 3 }, (_, index) => props.currentPage + index - 1),
    ])).filter(page => page >= 1 && page <= props.totalPages).sort((a, b) => a - b);
    const linkClass = 'inline-flex min-h-[44px] min-w-[44px] items-center justify-center border border-stone-300 px-3 py-2 text-sm text-stone-900 hover:bg-stone-100 focus-visible:outline focus-visible:outline-2';
    return (
      <nav aria-label="Pagination" className="flex flex-wrap items-center justify-center gap-2">
        {props.currentPage > 1 && <a href={getPageHref(props.currentPage - 1)} className={linkClass} rel="prev">{resolvedLabels.previous}</a>}
        {pages.map((page, index) => (
          <span key={page} className="inline-flex items-center gap-2">
            {index > 0 && page - pages[index - 1]! > 1 && <span aria-hidden="true">…</span>}
            <a href={getPageHref(page)} aria-label={`Page ${page}`} aria-current={page === props.currentPage ? 'page' : undefined}
              className={`${linkClass} ${page === props.currentPage ? 'bg-stone-900 !text-white hover:!bg-stone-800' : 'bg-white'}`}>
              {page}
            </a>
          </span>
        ))}
        {props.currentPage < props.totalPages && <a href={getPageHref(props.currentPage + 1)} className={linkClass} rel="next">{resolvedLabels.next}</a>}
      </nav>
    );
  }

  return (
    <div className="stone-pagination">
      <Pagination
        {...props}
        labels={resolvedLabels}
      />

      <style jsx global>{`
        .stone-pagination nav {
          font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .stone-pagination button {
          border-radius: 0 !important;
          font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          letter-spacing: 0.05em;
          transition: all 0.3s ease;
        }

        .stone-pagination button:not(:disabled):hover {
          background-color: rgb(245 245 244) !important;
          border-color: rgb(168 162 158) !important;
        }

        .stone-pagination button[aria-current="page"] {
          background-color: rgb(28 25 23) !important;
          color: white !important;
          border-color: rgb(28 25 23) !important;
          box-shadow: none !important;
        }

        .stone-pagination button:disabled {
          opacity: 0.3;
        }
      `}</style>
    </div>
  );
}

export default StonePagination;
